/**
 * Кнопка дизайн-системы Aptile на Mantine.
 *
 * Три роли — primary (главное действие экрана), secondary (равнозначное,
 * но не ведущее) и ghost (второстепенное, без фона). Роли различаются
 * видом, а не размером. Главное действие можно сделать «магнитным» —
 * оно слегка тянется за курсором.
 *
 * Цвет и тень роли заданы в теме (`BUTTON_ROLES`) утилитами Tailwind, а
 * не здесь: компонент только раскладывает их по пропсам Mantine, поэтому
 * вид кнопок правится в одном месте — `theme/mantineTheme.ts`.
 */

import { Button as MantineButton } from '@mantine/core';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { BUTTON_ROLES } from '../theme/mantineTheme';
import type { AppIcon } from './icons';
import { MagneticButton } from './bits/MagneticButton';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

interface ButtonProps extends NativeButtonProps {
  readonly variant?: ButtonVariant;
  readonly children: ReactNode;
  /** Растянуть на всю ширину — для одиночного действия под текстом. */
  readonly fullWidth?: boolean;
  /** Кнопка тянется за курсором; только для главного действия экрана. */
  readonly magnetic?: boolean;
  /** Иконка перед подписью. */
  readonly icon?: AppIcon;
  /**
   * Компактная кнопка урока: на телефоне — только иконка 48×48 (подпись
   * остаётся для экранного диктора), с `sm` — иконка и подпись. Нужна `icon`.
   */
  readonly compact?: boolean;
}

export function Button({
  variant = 'primary',
  fullWidth = false,
  magnetic = false,
  icon: Icon,
  compact = false,
  className,
  children,
  ...rest
}: ButtonProps) {
  const role = BUTTON_ROLES[variant];

  const button = (
    <MantineButton
      {...rest}
      variant={role.mantineVariant}
      color={role.color}
      radius="lg"
      size="lg"
      fullWidth={fullWidth}
      // Mantine держит кнопку фиксированной высоты и режет подпись в одну
      // строку — длинные русские подписи обрезались на узком экране. Здесь
      // подпись переносится, а кнопка растёт по высоте.
      h="auto"
      classNames={{
        inner: 'min-h-[3rem] py-2.5',
        label: 'whitespace-normal text-center leading-tight overflow-visible',
      }}
      className={[
        // Краска и отступы — утилитами Tailwind: сброс вне слоёв затирает
        // слоистые `padding`/`background`/`color` кнопки Mantine, поэтому
        // только непрослоенные классы реально видны.
        'btn-animate relative max-w-full overflow-hidden rounded-button px-5 sm:px-8',
        'font-heading text-button font-semibold',
        role.className,
        compact && Icon !== undefined ? 'max-sm:!px-3 max-sm:min-w-12' : '',
        className ?? '',
      ].join(' ')}
    >
      {Icon === undefined ? (
        children
      ) : (
        <span className="inline-flex items-center gap-2">
          <Icon size={22} stroke={2.2} aria-hidden="true" className="shrink-0" />
          <span className={compact ? 'max-sm:sr-only' : undefined}>{children}</span>
        </span>
      )}
    </MantineButton>
  );

  return magnetic ? (
    <MagneticButton className={fullWidth ? 'w-full' : undefined}>{button}</MagneticButton>
  ) : (
    button
  );
}
